/**
 * MCP tool catalog — the static definition of every tool the in-app bridge
 * exposes to external AI agents (docs/architecture/mcp-server/).
 *
 * Each entry carries the MCP tools/list contract (name, description,
 * inputSchema). The relay server forwards this catalog verbatim after the
 * app's hello handshake, so the agent sees exactly what the app supports.
 *
 * Granularity principle: `yap_execute_command` + `yap_run_script` give the
 * agent the ENTIRE typed command language (CAD, 3D, canonical presets,
 * ribbon registry, plugins) in one door. The structural tools around them
 * cover what a command line cannot: inspecting state, querying the scene,
 * reading the viewport pixels, and document-level file actions.
 */

import type { BridgeToolSpec } from './mcpBridgeProtocol';

const obj = (
    properties: Record<string, unknown>,
    required: string[] = [],
): Record<string, unknown> => ({
    type: 'object',
    properties,
    required,
    additionalProperties: false,
});

const str = (description: string) => ({ type: 'string', description });
const num = (description: string) => ({ type: 'number', description });
const bool = (description: string) => ({ type: 'boolean', description });
const strArr = (description: string) => ({
    type: 'array',
    items: { type: 'string' },
    description,
});

export const MCP_TOOL_CATALOG: BridgeToolSpec[] = [
    {
        name: 'yap_get_state',
        description:
            'Get the current application state: active workspace mode, active tool, selection ids, entity/layer/view counts, document name and dirty flag, the BIM tool options (bim.toolProperties — the Options Bar line), the CAD paper-space state (space: model|paper + layouts + viewport counts), and the 2D system variables (sysvars: PICKFIRST, FILLMODE, EDGEMODE, PDMODE, FILLETRADIUS...). Call this first to orient yourself.',
        inputSchema: obj({}),
    },
    {
        name: 'yap_get_system_prompt',
        description:
            'Fetch the user-authored AI agent system prompt (Settings → AI Agent Prompt) as categorized sections: persona/assumption engine, design concept, spatial program, materials, visualization prompts and modeling constraints. Returns JSON {enabled, prompt, sections}; use "prompt" as an extra system-level instruction when enabled, or fall back to built-in behavior when empty.',
        inputSchema: obj({}),
    },
    {
        name: 'yap_list_commands',
        description:
            'List every command the application accepts via yap_execute_command: canonical CAD/3D commands from the active preset (with their typed names and implementation status) plus legacy parser commands. Use to discover what you can run.',
        inputSchema: obj({
            filter: str('Optional case-insensitive substring to narrow the list (name or label).'),
            section: str('Optional section filter, e.g. draw, modify, solid, view, layer.'),
            limit: { type: 'integer', minimum: 1, maximum: 2000, description: 'Max entries returned (default 200).' },
        }),
    },
    {
        name: 'yap_execute_command',
        description:
            'Execute one command line exactly as if the user typed it into the command line. Resolves through the full chain: active preset → CAD parser → command registry → plugins. Returns success flag and the app-facing message. This is the universal door to every drawing, modify, view and system command.',
        inputSchema: obj(
            {
                command: str("Raw command line, e.g. 'LINE', 'ZOOM E', 'wall', 'UNITS'."),
                args: strArr('Optional extra whitespace-separated arguments appended to the line.'),
            },
            ['command'],
        ),
    },
    {
        name: 'yap_run_script',
        description:
            'Run a sequence of command lines sequentially through yap_execute_command (macro replay). Stops at the first failure unless continueOnError is set. Returns per-line results.',
        inputSchema: obj(
            {
                lines: strArr('Command lines in execution order.'),
                continueOnError: bool('Keep going after a failed line (default false).'),
            },
            ['lines'],
        ),
    },
    {
        name: 'yap_query_scene',
        description:
            'Query the cross-workbench scene ledger: every object in the document (BIM elements, CAD entities, NURBS shapes) with id, source, kind, name, layer, visibility, lock state and placement. Supports filters; returns compact summaries.',
        inputSchema: obj({
            source: { type: 'string', enum: ['bim', 'cad', 'nurbs'], description: 'Only objects owned by this workbench.' },
            kind: str('Only objects of this semantic kind, e.g. wall, line, door.'),
            nameContains: str('Case-insensitive substring match on display name.'),
            includeHidden: bool('Include invisible/locked records (default true).'),
            limit: { type: 'integer', minimum: 1, maximum: 5000, description: 'Max records (default 200).' },
        }),
    },
    {
        name: 'yap_get_element',
        description:
            'Fetch one object in full: BIM element (category, type, all parameters, phasing) or CAD entity (type and geometry data), searched across every store by id.',
        inputSchema: obj({ id: str('Element / entity id from yap_query_scene.') }, ['id']),
    },
    {
        name: 'yap_create_element',
        description:
            'Create a BIM model element directly. Undoable; returns the new element id. '
            + 'Walls: pass start + end as [x, y, z] points in metres (required); the render payload is built for you and wall joins re-solve automatically. '
            + 'Floors / Ceilings: pass boundary as 3+ [x, y] points (metres, counterclockwise) plus optional levelId (level id or name), baseOffset and thickness (metres); the render payload (boundary + heightOffsetFromLevel + layers) is built for you. '
            + 'Roofs: pass footprint (or boundary alias) as 3+ points plus optional roofType (flat | gable | hip | shed), slope (degrees), levelId, baseOffsetFromLevel, thickness. '
            + 'Levels: pass name + elevation in MILLIMETRES (e.g. { "category": "Levels", "name": "Level 2", "elevation": 3200 }); commits through the project datum layer, no element id in the scene. '
            + 'Dimensions: pass refs (2+ [x, y, z] points) plus optional offset (metres) and text; an aligned dimension payload is built for you. '
            + 'Doors / Windows: pass hostWallId (an existing wall id) plus position — either a 0–1 ratio along the host wall or a world [x, y, z] point that is projected onto it. '
            + 'Other categories: parameters + optional position [x, y, z]; annotation categories (Tags, Text Notes) additionally accept a geometryData render payload — without it the renderer has nothing to draw. Prefer interactive commands for sketched geometry; use this for batch/programmatic creation.',
        inputSchema: obj(
            {
                category: str("BIM category, e.g. 'Walls', 'Doors', 'Windows', 'Structural Framing', 'Dimensions'."),
                type: str('Family/type name as "Family: Type", e.g. "Basic Wall: Generic - 200mm" (defaults per category: walls "Basic Wall: Generic - 200mm", doors "Single-Flush: 0915 x 2134mm", windows "Fixed: 0915 x 1830mm").'),
                name: str('Element name (auto-numbered when omitted).'),
                parameters: { type: 'object', description: 'Initial parameter map (string keys, JSON values).', additionalProperties: true },
                position: { type: ['number', 'array'], description: 'Placement. Other categories: [x, y, z] in model units. Doors/Windows: either a number (0–1 ratio along the host wall) or a world [x, y, z] point projected onto the host.' },
                start: strArr('Walls: start point [x, y, z] in metres (required).'),
                end: strArr('Walls: end point [x, y, z] in metres (required).'),
                height: num('Walls/Doors/Windows: height in metres (walls default 3; openings default from the family type).'),
                thickness: num('Walls: thickness in metres (overrides the layer structure of the wall type).'),
                locationLine: str('Walls: WallCenterline | CoreCenterline | FinishFaceExterior | FinishFaceInterior | CoreFaceExterior | CoreFaceInterior (default WallCenterline).'),
                baseConstraintId: str('Walls: base level id or name.'),
                topConstraintId: str("Walls: top level id or name, or 'Unconnected' (default)."),
                baseOffset: num('Walls: base offset in metres (default 0).'),
                topOffset: num('Walls: top offset in metres (default 0).'),
                structural: bool('Walls: structural flag (default false).'),
                hostWallId: str('Doors/Windows: id of the host wall (required; ids come from yap_create_element or yap_query_scene).'),
                point: strArr('Doors/Windows: alternative world [x, y, z] point projected onto the host wall.'),
                sillHeight: num('Doors/Windows: sill height in metres (doors default 0, windows 0.915).'),
                width: num('Doors/Windows: width in metres (default from the family type).'),
                geometryData: { type: 'object', description: 'Optional geometry payload for annotation/model elements (kind-specific field bag, e.g. refs/center/rim for dimensions).', additionalProperties: true },
            },
            ['category'],
        ),
    },
    {
        name: 'yap_update_element',
        description:
            'Modify an existing object: rename, toggle visibility/lock, merge BIM parameters, or patch CAD entity data fields. Works for both BIM elements and CAD entities by id.',
        inputSchema: obj(
            {
                id: str('Object id.'),
                name: str('New display name.'),
                visible: bool('Set visibility (CAD entity / ledger record).'),
                locked: bool('Set lock state.'),
                parameters: { type: 'object', description: 'BIM parameters to merge.', additionalProperties: true },
                data: { type: 'object', description: 'CAD entity data fields to merge (e.g. start, end, radius).', additionalProperties: true },
            },
            ['id'],
        ),
    },
    {
        name: 'yap_delete_elements',
        description: 'Delete objects by id (BIM elements and/or CAD entities). Undoable via edit.undo.',
        inputSchema: obj({ ids: strArr('Ids to delete.') }, ['ids']),
    },
    {
        name: 'yap_select_elements',
        description:
            'Set the selection: replace or add ids, select all, or clear. Reports the resulting selection count.',
        inputSchema: obj({
            ids: strArr('Element ids to select.'),
            mode: { type: 'string', enum: ['replace', 'add', 'clear', 'all'], description: 'Selection mode (default replace).' },
        }),
    },
    {
        name: 'yap_control_view',
        description:
            'Drive the viewport: set orientation (top/front/right/perspective), zoom to fit / selection / a corner window / a scale factor, pan by a world vector, switch display mode (wireframe/shaded/rendered/xray/ghosted/hiddenLine), toggle grid/axes, or set the camera pose directly. Mirrors the in-app view commands (CAD ZOOM W/S/PAN included).',
        inputSchema: obj({
            action: {
                type: 'string',
                enum: ['setView', 'zoomFit', 'zoomSelection', 'zoomRegion', 'zoomScale', 'pan', 'displayMode', 'toggleGrid', 'toggleAxes', 'setCamera'],
                description: 'Viewport operation to perform.',
            },
            view: { type: 'string', enum: ['top', 'front', 'right', 'perspective'], description: 'For setView.' },
            mode: { type: 'string', enum: ['wireframe', 'shaded', 'rendered', 'xray', 'ghosted', 'hiddenLine'], description: 'For displayMode.' },
            position: strArr('For setCamera: [x, y, z].'),
            target: strArr('For setCamera: orbit target [x, y, z].'),
            corner1: strArr('For zoomRegion: first window corner [x, y, z] (world ground).'),
            corner2: strArr('For zoomRegion: opposite window corner [x, y, z].'),
            factor: num('For zoomScale: zoom factor (>0; 2 = twice as close).'),
            dx: num('For pan: world X delta.'),
            dy: num('For pan: world Y delta.'),
        }),
    },
    {
        name: 'yap_set_tool_option',
        description:
            "Set one active-tool option (the in-app Options Bar line) before running a tool command: e.g. key 'dimensionTextPosition' (Above|Below|Center) for BIM Aligned/Linear dimensions, 'spotElevationType' (Absolute|Relative|Shared) for spot elevations, 'detailComponentSymbol' (Brick|Block|Bolt|Dot), 'symbolType' (North Arrow|Break Line|Centerline), 'filledRegionPattern', 'repeatingDetailSpacing' (mm), 'textSizeMm', 'textLeader' (None|Left|Right), 'tagOnPlacement' (bool), or 'wallHeight'. Applies to the BIM store's shared toolProperties, read live by the placement tools (annotation tools re-read them at click time). Returns the value now stored.",
        inputSchema: obj(
            {
                key: str('Tool-property key (see yap_get_state bim.toolProperties for the current set).'),
                value: {
                    type: ['string', 'number', 'boolean'],
                    description: 'New value. Enum keys must match the exact spelling (e.g. "Above", "Relative", "North Arrow").',
                },
            },
            ['key', 'value'],
        ),
    },
    {
        name: 'yap_screenshot',
        description:
            'Capture the current 3D/2D viewport as a PNG image. Use to visually verify what the model looks like after your edits.',
        inputSchema: obj({}),
    },
    {
        name: 'yap_list_layers',
        description: 'List drawing layers (CAD) and model categories present in the document with visibility/lock state.',
        inputSchema: obj({}),
    },
    {
        name: 'yap_manage_layers',
        description:
            'Create, rename or update a layer (color, visible, locked, frozen, linetype, lineweight, transparency), set the active layer, or purge unused layers and unreferenced block definitions. CAD-style layer management.',
        inputSchema: obj({
            action: { type: 'string', enum: ['create', 'update', 'setActive', 'purge'], description: 'Layer operation.' },
            layerId: str('Target layer id or name (create/update/setActive).'),
            name: str('New layer name (create/update).'),
            color: str('Hex color, e.g. #ff0000.'),
            visible: bool('Visibility flag.'),
            locked: bool('Lock flag.'),
            frozen: bool('Freeze flag (CAD): off + dropped from regen/snap.'),
            linetype: str('BYLAYER linetype: DASHED|CENTER|HIDDEN|DOTTED|PHANTOM|DASHDOT or empty for continuous.'),
            lineweight: num('Lineweight in hundredths of a mm (classic ACAD units).'),
            transparency: num('Transparency 0-100 percent.'),
        }),
    },
    {
        name: 'yap_document_action',
        description:
            'Document-level file actions: save the current project to its bound file/workspace, start a new empty project, open a project stored in the in-app workspace by name, or export the model as JSON/IFC/OBJ/DXF/SVG text. Returns the produced text for exports (not file dialogs).',
        inputSchema: obj(
            {
                action: { type: 'string', enum: ['save', 'new', 'open', 'export', 'info'], description: 'Document operation.' },
                name: str('For open: workspace document name. For new: project name.'),
                format: { type: 'string', enum: ['json', 'ifc', 'obj', 'dxf', 'svg'], description: 'For export (default json; dxf = ASCII DXF R12 of the CAD ledger, svg = vector drawing).' },
            },
            ['action'],
        ),
    },
    {
        name: 'yap_import_ifc',
        description:
            "Import IFC content as families: pass the full STEP text of an .ifc file (IFC is plain text, so the agent can read it from disk). Every tessellatable product in the file is registered as a static-mesh family in the Project Browser's Families tree (with its property sets as parameters), ready for yap_place_family. Mirrors the user's drag & drop of an .ifc onto the viewport. A bundled seed furniture set (sofa, bed, table, chair, kitchen counter, WC) ships with the app at public/families/seed-families.ifc — when the model needs interiors and no families are loaded, import it first (fetch its text via the workspace/code tools, or embed equivalent boxes yourself).",
        inputSchema: obj(
            {
                content: str('Full IFC file text (must start with the ISO-10303-21 header).'),
                fileName: str('Optional display label for the source file (default agent-import.ifc).'),
            },
            ['content'],
        ),
    },
    {
        name: 'yap_list_families',
        description:
            'List the live family registry: every loaded family with its category, type names and whether it is a static-mesh (IFC-imported) family placeable via yap_place_family. Supports category and name filters.',
        inputSchema: obj({
            category: str('Only families of this category, e.g. Doors, Furniture.'),
            nameContains: str('Case-insensitive substring match on family name.'),
            limit: { type: 'integer', minimum: 1, maximum: 2000, description: 'Max entries returned (default 200).' },
        }),
    },
    {
        name: 'yap_place_family',
        description:
            "Place a loaded static (IFC-imported) family instance into the model at exact coordinates in metres. Undoable. Z is the floor elevation; rotation is degrees around the vertical axis. Parametric (non-static) families are NOT placeable this way — use yap_execute_command for those.",
        inputSchema: obj(
            {
                family: str('Exact family name from yap_list_families.'),
                position: strArr('[x, y, z] placement in metres.'),
                rotation: { type: 'number', description: 'Yaw in degrees around Z (optional).' },
            },
            ['family', 'position'],
        ),
    },
    {
        name: 'yap_validate_design',
        description:
            'Design-completeness gate for building models: checks levels, a Floors slab covering each walled level, a Roof closing the top, shared wall corners, live door/window hosts and annotation presence. Returns a structured gap report (code, severity blocking|warning, detail). The deliverable is finished only when this reports ZERO blocking gaps — call it after creation batches and fix what it lists.',
        inputSchema: obj({}),
    },
    {
        name: 'yap_list_design_templates',
        description:
            'List the bundled design seed templates (single-story house, two-story house, studio apartment): parametric skeletons with levels, envelope, slabs, roof and core openings. Start one with yap_start_design and adapt it instead of modeling boilerplate geometry by hand.',
        inputSchema: obj({}),
    },
    {
        name: 'yap_start_design',
        description:
            'Start a design from a bundled seed template: creates levels, envelope walls, partitions, slabs, roof and openings in one call (template-local wall names are resolved automatically). Returns the created ids. Then continue the design playbook: adapt dimensions, add openings/interiors/annotations, and verify with yap_validate_design.',
        inputSchema: obj(
            { template: str("Template id from yap_list_design_templates, e.g. 'single-story-house'.") },
            ['template'],
        ),
    },
    {
        name: 'yap_annotate_plan',
        description:
            'One-call exterior annotation of the current model: derives the wall envelope and creates the exterior dimension chain (per segment + overall) on the south and west sides as real Dimensions elements. Level labels are drawn by the datum layer itself. Call after the envelope is complete; check yap_validate_design NO_DIMENSIONS clears.',
        inputSchema: obj({
            offset: num('Distance of the dim chains from the walls, in metres (default 0.8, overall at +0.6 more).'),
        }),
    },
    {
        name: 'yap_set_mode',
        description:
            "Switch the active workspace: 'bim' (BIM), '2d' (2D drafting) or '3d' (3D NURBS). Navigation changes which toolset and command presets feel native, but every command remains dispatchable from any mode.",
        inputSchema: obj({ mode: { type: 'string', enum: ['bim', '2d', '3d'] } }, ['mode']),
    },
];

/** Fast lookup by tool name for the router. */
export const MCP_TOOL_BY_NAME = new Map(MCP_TOOL_CATALOG.map((t) => [t.name, t]));
